<script setup lang="ts">
import { computed } from "vue"

import { formatPrice } from "@/lib/utils"

const props = defineProps<{
  maxAmount: number
  approvedAmount: number
  reservedAmount: number
  allowedAmount: number
}>()

const budgetTotal = computed(() => Math.max(Number(props.maxAmount) || 0, 0))
const approvedValue = computed(() =>
  Math.max(Number(props.approvedAmount) || 0, 0),
)
const reservedValue = computed(() =>
  Math.max(Number(props.reservedAmount) || 0, 0),
)
const remainingValue = computed(() =>
  Math.max(Number(props.allowedAmount) || 0, 0),
)

const approvedPercent = computed(() => {
  if (budgetTotal.value === 0) return 0

  return (approvedValue.value / budgetTotal.value) * 100
})

const reservedPercent = computed(() => {
  if (budgetTotal.value === 0) return 0

  return (reservedValue.value / budgetTotal.value) * 100
})

const remainingPercent = computed(() => {
  if (budgetTotal.value === 0) return 100

  const raw = 100 - approvedPercent.value - reservedPercent.value
  return Math.max(raw, 0)
})
</script>

<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between text-sm">
      <span class="text-gray-600">Límite anual</span>
      <span class="font-semibold">
        {{ formatPrice(maxAmount) }}
      </span>
    </div>
    <div class="flex items-center justify-between text-sm">
      <span class="text-gray-600">Pendiente de aprovación</span>
      <span class="font-semibold text-amber-500">
        {{ formatPrice(reservedAmount) }}
      </span>
    </div>
    <div class="flex items-center justify-between text-sm">
      <span class="text-gray-600">Aprobado</span>
      <span class="font-semibold text-green-600">
        {{ formatPrice(approvedAmount) }}
      </span>
    </div>
    <div class="flex items-center justify-between text-sm">
      <span class="text-gray-600">Disponible</span>
      <span class="font-semibold text-gray-600">
        {{ formatPrice(allowedAmount) }}
      </span>
    </div>

    <div
      class="flex h-3.5 w-full overflow-hidden rounded-full bg-gray-200"
      role="progressbar"
      aria-label="Uso del presupuesto"
    >
      <div
        class="h-full bg-[#118c6e] transition-all"
        :style="{ width: `${approvedPercent}%` }"
        title="Aprobado"
      />
      <div
        class="h-full bg-amber-500 transition-all"
        :style="{ width: `${reservedPercent}%` }"
        title="Pendiente de aprovación"
      />
      <div
        class="h-full bg-gray-300 transition-all"
        :style="{ width: `${remainingPercent}%` }"
        title="Disponible"
      />
    </div>

    <div class="flex items-center gap-4 text-xs text-gray-600">
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full bg-[#118c6e]" />
        Aprobado
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full bg-amber-500" />
        Pendiente de aprovación
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full bg-gray-300" />
        Disponible ({{ formatPrice(remainingValue) }})
      </span>
    </div>
  </div>
</template>
